import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import https from "https";
import axios from "axios";
import log from "../utils/log";
import metaData from "../data/metaData";
import c from "../utils/common";

//
import useAuthStore from "../states/useAuth";
import useRequestStore from "../states/useRequest";
import useShopStore from "../states/useShop";
import useDB from "../hooks/useDB";

const useShop = () => {
  const { auth, user } = useAuthStore();
  const {} = useRequestStore();
  const {
    getSingleShop: getSingleShopFromCache,
    getShops: getShopsFromCache,
    updateSingleShopCache,
    updateShopsCache,
  } = useShopStore();

  const { get } = useDB();

  /**
   * getSingleShop:
   * This function fetches a single shop by its ID, first checking if the shop's data is cached.
   *
   * - If the shop data is found in the cache, it returns the cached data.
   * - If the shop data is not found in the cache, it simulates a delay (e.g., 2000ms),
   *   fetches the data (simulated here by a temporary object), stores it in the cache,
   *   and then returns the fetched data.
   *
   * @param {number|string} id - The unique ID of the shop to fetch.
   * @returns {Object} - The shop data, either from the cache or fetched via API.
   */
  const getSingleShop = async (id, saveCache = true, useCache = true) => {
    if (!id) {
      return undefined;
    }

    let cache = useCache ? getSingleShopFromCache(id) : null;

    if (!cache) {
      log.debug("no cache, axios");

      let response = await get("shop/query", {
        id,
        populate: [
          {
            path: "company",
            select: "name br address",
          },
          {
            path: "brand",
            select: "name logo category",
          },
        ],
      });

      // Cache the fetched shop data
      if (saveCache) {
        updateSingleShopCache("update", [response]);
      }
      return response; // Return the fetched data
    } else {
      log.debug("using cache");
      return cache; // Return the cached data
    }
  };

  /**
   * getShops:
   * This function fetches a list of shops based on a query (such as pagination or filters),
   * first checking if the query result is cached.
   *
   * - If the query result is found in the cache, it returns the cached data.
   * - If the query result is not found in the cache, it simulates a delay (e.g., 2000ms),
   *   fetches the data (simulated here by a temporary array), stores it in the cache,
   *   and then returns the fetched data.
   *
   * @param {string|Object|undefined} query - The query used to filter or paginate the shops.
   * @returns {Array} - The list of shops, either from the cache or fetched via API.
   */
  const getShops = async (query, saveCache = true, useCache = true) => {
    let cache = useCache ? getShopsFromCache(query) : null;
    if (cache) {
      log.debug("using cache");
      return cache; // Return cached data if available
    }

    log.debug("no cache, axios");

    // prepare body
    let body;
    if (query === undefined || typeof query === "string") {
      //transform to json
      body = {
        companyId: user?.companyId,
        select: "-gallery -paymentMethod -email -website -openHour -description -phone",
        populate: {
          path: "brand",
          select: "logo",
        },
      };
    } else if (typeof query === "object") {
      // If query is an object, assume it's already in the correct format
      body = query;
    } else {
      throw new Error("Invalid query format. It must be either undefined, a string, or an object.");
    }

    let response = await get("shops/query", body);

    // Cache the fetched requests data
    if (typeof query !== "object" && saveCache) {
      updateShopsCache("update", query, response); // Cache the fetched shops data
    }
    return response; // Return the fetched data
  };

  const updateShop = async (shopId, data,) => {
    const response = await axios.patch(metaData.serverURL + "shop", data, {
      params: { id: shopId }, // Pass the request ID as a query parameter if needed
      headers: { Authorization: auth.jwt },
      httpsAgent: new https.Agent({ keepAlive: true, keepAliveMsecs: 60000 }),
    });
    // log.debug(response.data);
    return response.data; // Return the updated data
  };

  const getAllianceShop = async (companyId) => {
    const query = {
      $or: [
        { companyId: companyId },
        { allianceIds: { $in: [companyId] } }, // Check if companyId exists in allianceIds array
      ],
      select: "-gallery -paymentMethod -email -website -openHour -description -phone",
      populate: [
        {
          path: "company",
          select: "name",
        },
        {
          path: "brand",
          select: "name logo",
        },
      ],
    };

    // Call getShops with the alliance-specific query
    return await getShops(query, false, false);
  };

  const addAllianceShop = async (shopId, companyId) => {
    let data = { id: shopId, companyId, action: "add" };
    const response = await axios.post(metaData.serverURL + "shop/allianceShop", data, {
      headers: { Authorization: auth.jwt },
      httpsAgent: new https.Agent({ keepAlive: true, keepAliveMsecs: 60000 }),
    });
    // log.debug(response.data);
    return response.data; // Return the updated data
  };

  const deleteAllianceShop = async (shopId, companyId) => {
    let data = { id: shopId, companyId, action: "delete" };
    const response = await axios.post(metaData.serverURL + "shop/allianceShop", data, {
      headers: { Authorization: auth.jwt },
      httpsAgent: new https.Agent({ keepAlive: true, keepAliveMsecs: 60000 }),
    });
    // log.debug(response.data);
    return response.data; // Return the updated data
  };

  return { getSingleShop, getShops, updateShop, getAllianceShop, addAllianceShop, deleteAllianceShop };
};

export default useShop;
